/**
 * Detects, in the browser, whether the page it is running in was server-rendered.
 *
 * WHY THIS EXISTS (BBS-0070)
 *
 * The client redirect shim (`redirect.shim.ts`) is only needed on SPA builds,
 * where no server middleware sits in the request path. On an SSR build
 * `createRedirectMiddleware` in `server.ts` has already applied every redirect
 * before the HTML was sent, so running the shim again in the browser is pure
 * duplicated work — and expensive work: it downloads a ~6 MB map from a blocking
 * app initializer, holding hydration on a slow connection while the page sits
 * there looking finished but with nothing clickable.
 *
 * WHY NOT AN `environment` FLAG
 *
 * Because the SSR and SPA builds SHARE their environment file. `angular.json`
 * defines both a `local` (`ssr: false`) and a `local-ssr` configuration, and both
 * resolve `@environment/environment` to the same `environment.local.ts`. A
 * `ssrEnabled` flag there would have to be simultaneously true and false. The
 * same collision exists for any future paired configuration, so the signal has to
 * come from the running page, not from build-time constants.
 *
 * TWO SIGNALS, EITHER OF WHICH MEANS "SERVER-RENDERED"
 *
 *   1. A marker stamped into `TransferState` by the server (`app.config.server.ts`)
 *      and serialised into the `ng-state` script tag. Explicit and intentional.
 *   2. `<app-root>` already having content. `src/index.html` ships it EMPTY, so
 *      anything inside it at app-initializer time — initializers run before the
 *      root component is created — was rendered by the server.
 *
 * Signal 2 is not redundant. It keeps the detection correct if the transfer-state
 * script is ever stripped by a proxy, or dropped by a future Angular change, and
 * it errs in the safe direction: the failure mode of a missed marker is a wasted
 * download, so an independent confirmation is worth having.
 */
import { StateKey, TransferState, makeStateKey } from '@angular/core';

/** Set by the server render; absent on a SPA build. */
export const SSR_ACTIVE: StateKey<boolean> = makeStateKey<boolean>('ssrActive');

/**
 * True when this page was server-rendered.
 *
 * Fails SAFE: when the page cannot be inspected at all the answer is `true`,
 * i.e. skip the shim. An unnecessary skip costs a redirect on an old URL in an
 * environment that is almost certainly SSR anyway; an unnecessary run costs every
 * visitor a multi-megabyte download that holds hydration.
 */
export function isServerRenderedPage(state: TransferState): boolean {
  if (state.get(SSR_ACTIVE, false)) {
    return true;
  }

  try {
    const appRoot = document.querySelector('app-root');

    // No `<app-root>` at all is not a situation this shim understands — skip.
    if (!appRoot) {
      return true;
    }

    return appRoot.hasChildNodes();
  } catch {
    return true;
  }
}
